import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlignCenter, AlignLeft, AlignRight, Bold, ChevronDown, ClipboardPaste, Copy,
  FilterX, ImagePlus, Italic, Link2, Paintbrush, PaintBucket, Plus, Redo2, Scissors,
  Search, Strikethrough, TableCellsMerge, Underline, Undo2, WrapText,
} from 'lucide-react';
import { api, type CellStyle, type Column, type Rec } from '../lib/api';
import { R, ft } from '../lib/ui';
import { ColorButton, ToolButton, ToolDivider } from './ToolButton';
import { AnchoredPopover } from './AnchoredPopover';
import { useToast } from './Toast';
import type { Sel } from './Grid';

const VERTICAL: { value: NonNullable<CellStyle['v']>; title: string; glyph: string }[] = [
  { value: 'top', title: 'По верхнему краю', glyph: '⤒' },
  { value: 'middle', title: 'По центру по вертикали', glyph: '↕' },
  { value: 'bottom', title: 'По нижнему краю', glyph: '⤓' },
];

/** Выравнивание по вертикали: одна кнопка, иконка меняется по состоянию. */
function VerticalAlign({
  value, disabled, onChange,
}: {
  value: CellStyle['v'];
  disabled?: boolean;
  onChange: (value: NonNullable<CellStyle['v']>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  const current = VERTICAL.find((item) => item.value === value);

  return (
    <>
      <button
        ref={ref}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        title="Выравнивание по вертикали"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          padding: '0 6px',
          height: 26,
          color: value ? R.accent : R.text,
          background: open || value ? R.accentSubtle : 'transparent',
          border: `1px solid ${open || value ? R.accent : 'transparent'}`,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          flex: '0 0 auto',
        }}
      >
        <span style={{ fontSize: 14, lineHeight: 1 }}>{current?.glyph ?? '↕'}</span>
        <ChevronDown size={10} />
      </button>

      <AnchoredPopover anchorRef={ref} open={open} onClose={() => setOpen(false)}>
        <div style={{ background: R.bgPanel, border: `2px solid ${R.border}`, padding: '4px 0' }}>
          {VERTICAL.map((item) => (
            <button
              key={item.value}
              onClick={() => {
                onChange(item.value);
                setOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '6px 12px',
                fontSize: 12.5,
                textAlign: 'left',
                color: value === item.value ? R.accent : R.text,
                background: value === item.value ? R.accentSubtle : 'transparent',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 14 }}>{item.glyph}</span>
              {item.title}
            </button>
          ))}
        </div>
      </AnchoredPopover>
    </>
  );
}

const FONT_FAMILIES = [
  { name: 'Archivo', label: 'Archivo (Стандарт)', preview: 'Archivo, sans-serif' },
  { name: 'Inter', label: 'Inter (Современный)', preview: 'Inter, sans-serif' },
  { name: 'Roboto', label: 'Roboto', preview: 'Roboto, sans-serif' },
  { name: 'Montserrat', label: 'Montserrat', preview: 'Montserrat, sans-serif' },
  { name: 'Playfair Display', label: 'Playfair Display (Serif)', preview: 'Playfair Display, serif' },
  { name: 'JetBrains Mono', label: 'JetBrains Mono (Код)', preview: 'JetBrains Mono, monospace' },
  { name: 'Arial', label: 'Arial', preview: 'Arial, sans-serif' },
  { name: 'Georgia', label: 'Georgia', preview: 'Georgia, serif' },
  { name: 'Times New Roman', label: 'Times New Roman', preview: 'Times New Roman, serif' },
  { name: 'Courier New', label: 'Courier New', preview: 'Courier New, monospace' },
];

const FONT_SIZES = [9, 10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32];

function FontFamilyPicker({
  value,
  disabled,
  onChange,
}: {
  value?: string;
  disabled?: boolean;
  onChange: (ff: string | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const current = FONT_FAMILIES.find(
    (f) => f.name.toLowerCase() === (value ?? 'Archivo').toLowerCase(),
  ) ?? {
    name: value ?? 'Archivo',
    label: value ?? 'Archivo',
    preview: value ? `${value}, sans-serif` : 'Archivo, sans-serif',
  };

  return (
    <>
      <button
        ref={ref}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        title="Шрифт текста"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 6,
          padding: '0 8px',
          height: 28,
          minWidth: 110,
          maxWidth: 135,
          fontSize: 12,
          fontFamily: current.preview,
          color: disabled ? R.textMuted : R.text,
          background: open ? R.bgElevated : 'transparent',
          border: ft.edge,
          borderRadius: 4,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.45 : 1,
          flex: '0 0 auto',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {current.name}
        </span>
        <ChevronDown size={11} style={{ flexShrink: 0, opacity: 0.6 }} />
      </button>

      <AnchoredPopover anchorRef={ref} open={open} onClose={() => setOpen(false)}>
        <div
          style={{
            width: 204,
            maxHeight: 280,
            overflowY: 'auto',
            background: R.bgPanel,
            border: `2px solid ${R.border}`,
            borderRadius: 6,
            padding: '4px 0',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          }}
          className="rt-scroll"
        >
          {FONT_FAMILIES.map((font) => {
            const isSelected =
              font.name.toLowerCase() === (value ?? 'Archivo').toLowerCase();
            return (
              <button
                key={font.name}
                onClick={() => {
                  onChange(font.name === 'Archivo' ? undefined : font.name);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '7px 12px',
                  fontSize: 13,
                  fontFamily: font.preview,
                  textAlign: 'left',
                  color: isSelected ? R.accent : R.text,
                  background: isSelected ? R.accentSubtle : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.background = R.bgElevated;
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.background = 'transparent';
                }}
              >
                <span>{font.label}</span>
                {isSelected && <span style={{ color: R.accent, fontSize: 12 }}>✓</span>}
              </button>
            );
          })}
        </div>
      </AnchoredPopover>
    </>
  );
}

function FontSizePicker({
  value = 13,
  disabled,
  onChange,
}: {
  value?: number;
  disabled?: boolean;
  onChange: (fs: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [textValue, setTextValue] = useState(String(value || 13));
  const ref = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const currentSize = value || 13;

  useEffect(() => {
    setTextValue(String(value || 13));
  }, [value]);

  const commitText = (raw: string) => {
    const parsed = parseInt(raw.trim(), 10);
    if (!isNaN(parsed) && parsed >= 6 && parsed <= 96) {
      onChange(parsed);
      setTextValue(String(parsed));
    } else {
      setTextValue(String(currentSize));
    }
  };

  const stepDown = () => {
    const next = Math.max(6, currentSize - 1);
    onChange(next);
    setTextValue(String(next));
  };

  const stepUp = () => {
    const next = Math.min(96, currentSize + 1);
    onChange(next);
    setTextValue(String(next));
  };

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 28,
        border: ft.edge,
        borderRadius: 4,
        background: R.bgInput,
        flex: '0 0 auto',
      }}
    >
      <button
        disabled={disabled || currentSize <= 6}
        onClick={stepDown}
        title="Уменьшить шрифт"
        style={{
          width: 20,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          borderRight: ft.edge,
          color: disabled ? R.textMuted : R.textSecondary,
          cursor: disabled ? 'default' : 'pointer',
          fontSize: 14,
          fontWeight: 700,
          userSelect: 'none',
          padding: 0,
        }}
      >
        −
      </button>

      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        disabled={disabled}
        value={textValue}
        onChange={(e) => setTextValue(e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={() => commitText(textValue)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitText(textValue);
            inputRef.current?.blur();
          } else if (e.key === 'Escape') {
            setTextValue(String(currentSize));
            inputRef.current?.blur();
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            stepUp();
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            stepDown();
          }
        }}
        title="Размер шрифта (введите число и нажмите Enter)"
        style={{
          width: 30,
          height: '100%',
          textAlign: 'center',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: disabled ? R.textMuted : R.text,
          fontSize: 12,
          fontWeight: 600,
          fontFamily: 'inherit',
          padding: 0,
          cursor: disabled ? 'default' : 'text',
        }}
      />

      <button
        ref={ref}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        title="Выбрать размер шрифта из списка"
        style={{
          width: 16,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: open ? R.bgElevated : 'transparent',
          border: 'none',
          borderLeft: ft.edge,
          borderRight: ft.edge,
          color: disabled ? R.textMuted : R.textMuted,
          cursor: disabled ? 'default' : 'pointer',
          padding: 0,
        }}
      >
        <ChevronDown size={10} style={{ opacity: 0.7 }} />
      </button>

      <button
        disabled={disabled || currentSize >= 96}
        onClick={stepUp}
        title="Увеличить шрифт"
        style={{
          width: 20,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          color: disabled ? R.textMuted : R.textSecondary,
          cursor: disabled ? 'default' : 'pointer',
          fontSize: 14,
          fontWeight: 700,
          userSelect: 'none',
          padding: 0,
        }}
      >
        +
      </button>

      <AnchoredPopover anchorRef={ref} open={open} onClose={() => setOpen(false)}>
        <div
          style={{
            width: 76,
            maxHeight: 220,
            overflowY: 'auto',
            background: R.bgPanel,
            border: `2px solid ${R.border}`,
            borderRadius: 6,
            padding: '4px 0',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          }}
          className="rt-scroll"
        >
          {FONT_SIZES.map((size) => {
            const isSelected = size === currentSize;
            return (
              <button
                key={size}
                onClick={() => {
                  onChange(size);
                  setTextValue(String(size));
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  padding: '5px 8px',
                  fontSize: 12.5,
                  fontWeight: isSelected ? 700 : 500,
                  color: isSelected ? R.accent : R.text,
                  background: isSelected ? R.accentSubtle : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.background = R.bgElevated;
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.background = 'transparent';
                }}
              >
                {size}
              </button>
            );
          })}
        </div>
      </AnchoredPopover>
    </div>
  );
}

export function FormatToolbar({
  docId, tableId, columns, records, sel,
  canUndo, canRedo, undoLabel, redoLabel, onUndo, onRedo,
  onSearch, onClearSortFilter, sortFilterActive,
  onAddRow, onAddColumn, onInsertImage, onInsertLink,
}: {
  docId: string;
  tableId: string;
  columns: Column[];
  records: Rec[];
  sel: Sel | null;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel?: string;
  redoLabel?: string;
  onUndo: () => void;
  onRedo: () => void;
  onSearch: () => void;
  onClearSortFilter: () => void;
  sortFilterActive: boolean;
  onAddRow: () => void;
  onAddColumn: () => void;
  onInsertImage: () => void;
  onInsertLink: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  /** Формат в буфере: пока он есть, следующий клик по ячейке его применит. */
  const [brush, setBrush] = useState<CellStyle | null>(null);

  const styles = useQuery({
    queryKey: ['styles', docId, tableId],
    queryFn: () => api.cellStyles(docId, tableId),
    enabled: !!tableId,
  });

  const merges = useQuery({
    queryKey: ['merges', docId, tableId],
    queryFn: () => api.cellMerges(docId, tableId),
    enabled: !!tableId,
  });

  const styleMap = useMemo(() => {
    const map = new Map<string, CellStyle>();
    for (const s of styles.data ?? []) map.set(`${s.recordId}:${s.colId}`, s.style);
    return map;
  }, [styles.data]);

  /** Стиль ячейки под курсором — по нему подсвечиваются кнопки. */
  const active: CellStyle = useMemo(() => {
    if (!sel) return {};
    const rec = records[sel.r1];
    const col = columns[sel.c1];
    if (!rec || !col) return {};
    return styleMap.get(`${rec.id}:${col.fields.colId}`) ?? {};
  }, [sel, records, columns, styleMap]);

  const patchStyles = useMutation({
    mutationFn: (updates: { recordId: string; colId: string; style: CellStyle | null }[]) =>
      api.patchCellStyles(docId, tableId, updates),
    onMutate: async (updates) => {
      await qc.cancelQueries({ queryKey: ['styles', docId, tableId] });
      const previous = qc.getQueryData<{ recordId: string; colId: string; style: CellStyle }[]>([
        'styles', docId, tableId,
      ]);

      qc.setQueryData<{ recordId: string; colId: string; style: CellStyle }[]>(
        ['styles', docId, tableId],
        (old = []) => {
          const map = new Map(old.map((item) => [`${item.recordId}:${item.colId}`, item.style]));
          for (const u of updates) {
            const k = `${u.recordId}:${u.colId}`;
            if (u.style === null) map.delete(k);
            else map.set(k, u.style);
          }
          return Array.from(map.entries()).map(([key, style]) => {
            const [recordId, colId] = key.split(':');
            return { recordId, colId, style };
          });
        },
      );

      return { previous };
    },
    onError: (e, _, context) => {
      if (context?.previous) {
        qc.setQueryData(['styles', docId, tableId], context.previous);
      }
      toast((e as Error).message, 'err');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['styles', docId, tableId] });
    },
  });

  const patchMerges = useMutation({
    mutationFn: (body: {
      merges?: { topRecordId: string; leftColId: string; rowspan: number; colspan: number }[];
      unmerges?: { topRecordId: string; leftColId: string }[];
    }) => api.patchCellMerges(docId, tableId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['merges', docId, tableId] }),
    onError: (e) => toast((e as Error).message, 'err'),
  });

  /** Правка применяется поверх текущего стиля каждой ячейки выделения. */
  const apply = (patch: CellStyle) => {
    if (!sel) return;
    const updates: { recordId: string; colId: string; style: CellStyle | null }[] = [];

    for (let r = sel.r1; r <= sel.r2; r++) {
      const rec = records[r];
      if (!rec) continue;
      for (let c = sel.c1; c <= sel.c2; c++) {
        const col = columns[c];
        if (!col) continue;

        const key = `${rec.id}:${col.fields.colId}`;
        const next: CellStyle = { ...(styleMap.get(key) ?? {}), ...patch };
        for (const k of Object.keys(next) as (keyof CellStyle)[]) {
          if (next[k] === undefined) delete next[k];
        }
        updates.push({
          recordId: rec.id,
          colId: col.fields.colId,
          style: Object.keys(next).length ? next : null,
        });
      }
    }
    if (updates.length) patchStyles.mutate(updates);
  };

  /** Кисть формата: копируем стиль активной ячейки и ждём клика. */
  useEffect(() => {
    if (!brush || !sel) return;
    apply(brush);
    setBrush(null);
    // Реагируем именно на смену выделения — это и есть «клик по ячейке».
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel?.r1, sel?.c1]);

  useEffect(() => {
    document.body.style.cursor = brush ? 'copy' : '';
    return () => {
      document.body.style.cursor = '';
    };
  }, [brush]);

  // Ctrl+B / I / U — как в оригинале.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey || !sel) return;
      const key = e.key.toLowerCase();
      if (key === 'b') { e.preventDefault(); apply({ b: active.b ? undefined : 1 }); }
      if (key === 'i') { e.preventDefault(); apply({ i: active.i ? undefined : 1 }); }
      if (key === 'u') { e.preventDefault(); apply({ u: active.u ? undefined : 1 }); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const anchor = sel
    ? { topRecordId: records[sel.r1]?.id, leftColId: columns[sel.c1]?.fields.colId }
    : null;

  const selectionMerge =
    anchor?.topRecordId && anchor.leftColId
      ? merges.data?.find(
          (m) => m.topRecordId === anchor.topRecordId && m.leftColId === anchor.leftColId,
        )
      : undefined;

  const multi = !!sel && (sel.r1 !== sel.r2 || sel.c1 !== sel.c2);

  const toggleMerge = () => {
    if (!sel || !anchor?.topRecordId || !anchor.leftColId) return;

    if (selectionMerge) {
      patchMerges.mutate({
        unmerges: [
          { topRecordId: selectionMerge.topRecordId, leftColId: selectionMerge.leftColId },
        ],
      });
      return;
    }
    if (!multi) return;

    // Всё, что пересекается с новым объединением, сначала снимаем.
    const overlapping = (merges.data ?? []).filter((m) => {
      const r = records.findIndex((rec) => rec.id === m.topRecordId);
      const c = columns.findIndex((col) => col.fields.colId === m.leftColId);
      if (r < 0 || c < 0) return false;
      return !(
        r + m.rowspan - 1 < sel.r1 || r > sel.r2 || c + m.colspan - 1 < sel.c1 || c > sel.c2
      );
    });

    patchMerges.mutate({
      unmerges: overlapping.map((m) => ({
        topRecordId: m.topRecordId,
        leftColId: m.leftColId,
      })),
      merges: [
        {
          topRecordId: anchor.topRecordId,
          leftColId: anchor.leftColId,
          rowspan: sel.r2 - sel.r1 + 1,
          colspan: sel.c2 - sel.c1 + 1,
        },
      ],
    });
  };

  /**
   * Буфер обмена живёт в сетке — она знает про выделение и объединения.
   * Кнопки шлют то же событие, что и клавиатура, вместо своей копии логики.
   */
  const sendShortcut = (key: string) =>
    window.dispatchEvent(new KeyboardEvent('keydown', { key, ctrlKey: true, bubbles: true }));

  const off = !sel;

  return (
    <div
      className="rt-scroll rt-toolbar"
      style={{
        minHeight: 42,
        padding: '0 16px',
        borderBottom: ft.strong,
        background: R.bgSubtle,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        flex: '0 0 auto',
        overflowX: 'auto',
      }}
    >
      <ToolButton
        icon={Undo2}
        title={canUndo ? `Отменить: ${undoLabel ?? 'правку'} (Ctrl+Z)` : 'Нечего отменять'}
        disabled={!canUndo}
        onClick={onUndo}
      />
      <ToolButton
        icon={Redo2}
        title={canRedo ? `Повторить: ${redoLabel ?? 'правку'} (Ctrl+Y)` : 'Нечего повторять'}
        disabled={!canRedo}
        onClick={onRedo}
      />
      <ToolButton
        icon={Paintbrush}
        title={brush ? 'Клик по ячейке для вставки' : 'Копировать формат'}
        active={!!brush}
        disabled={off}
        onClick={() => setBrush(brush ? null : active)}
      />

      <ToolDivider />

      <ToolButton icon={Search} title="Поиск (Ctrl+F)" onClick={onSearch} />
      <ToolButton
        icon={FilterX}
        title={
          sortFilterActive
            ? 'Сбросить сортировку/фильтр'
            : 'Сортировка и фильтры включены — используйте стрелки в шапке колонок'
        }
        active={sortFilterActive}
        disabled={!sortFilterActive}
        onClick={onClearSortFilter}
      />

      <ToolDivider />

      <ToolButton icon={Plus} label="Строка" title="Добавить строку" onClick={onAddRow} />
      <ToolButton icon={Plus} label="Колонка" title="Добавить колонку" onClick={onAddColumn} />

      <ToolDivider />

      <ToolButton
        icon={Copy}
        title="Копировать (Ctrl+C)"
        disabled={off}
        onClick={() => sendShortcut('c')}
      />
      <ToolButton
        icon={Scissors}
        title="Вырезать (Ctrl+X)"
        disabled={off}
        onClick={() => sendShortcut('x')}
      />
      <ToolButton
        icon={ClipboardPaste}
        title="Вставить (Ctrl+V)"
        disabled={off}
        onClick={() => sendShortcut('v')}
      />

      <ToolDivider />

      <FontFamilyPicker
        value={active.ff}
        disabled={off}
        onChange={(ff) => apply({ ff })}
      />

      <FontSizePicker
        value={active.fs}
        disabled={off}
        onChange={(fs) => apply({ fs })}
      />

      <ToolDivider />

      <ToolButton
        icon={Bold}
        title="Полужирный (Ctrl+B)"
        active={!!active.b}
        disabled={off}
        onClick={() => apply({ b: active.b ? undefined : 1 })}
      />
      <ToolButton
        icon={Italic}
        title="Курсив (Ctrl+I)"
        active={!!active.i}
        disabled={off}
        onClick={() => apply({ i: active.i ? undefined : 1 })}
      />
      <ToolButton
        icon={Underline}
        title="Подчёркнутый (Ctrl+U)"
        active={!!active.u}
        disabled={off}
        onClick={() => apply({ u: active.u ? undefined : 1 })}
      />
      <ToolButton
        icon={Strikethrough}
        title="Зачёркнутый"
        active={!!active.s}
        disabled={off}
        onClick={() => apply({ s: active.s ? undefined : 1 })}
      />

      <ColorButton
        icon={<span style={{ fontSize: 13, fontWeight: 700 }}>A</span>}
        value={active.fg}
        title="Цвет текста"
        disabled={off}
        onChange={(fg) => apply({ fg })}
      />
      <ColorButton
        icon={<PaintBucket size={14} />}
        value={active.bg}
        title="Цвет заливки"
        disabled={off}
        onChange={(bg) => apply({ bg })}
      />

      <ToolDivider />

      <ToolButton
        icon={AlignLeft}
        title="По левому краю"
        active={active.h === 'left'}
        disabled={off}
        onClick={() => apply({ h: active.h === 'left' ? undefined : 'left' })}
      />
      <ToolButton
        icon={AlignCenter}
        title="По центру"
        active={active.h === 'center'}
        disabled={off}
        onClick={() => apply({ h: active.h === 'center' ? undefined : 'center' })}
      />
      <ToolButton
        icon={AlignRight}
        title="По правому краю"
        active={active.h === 'right'}
        disabled={off}
        onClick={() => apply({ h: active.h === 'right' ? undefined : 'right' })}
      />

      <VerticalAlign
        value={active.v}
        disabled={off}
        onChange={(v) => apply({ v: active.v === v ? undefined : v })}
      />

      <ToolButton
        icon={WrapText}
        title="Перенос текста"
        active={!!active.w}
        disabled={off}
        onClick={() => apply({ w: active.w ? undefined : 1 })}
      />

      <ToolButton
        icon={Link2}
        title="Вставить ссылку"
        disabled={off}
        onClick={onInsertLink}
      />

      <ToolButton
        icon={ImagePlus}
        title="Вставить картинку (до 1 МБ)"
        disabled={off}
        onClick={onInsertImage}
      />

      <ToolButton
        icon={TableCellsMerge}
        title={selectionMerge ? 'Разъединить' : 'Объединить ячейки'}
        active={!!selectionMerge}
        disabled={off || (!multi && !selectionMerge)}
        onClick={toggleMerge}
      />
    </div>
  );
}
